/**
 * ActivityFeed
 *
 * Real-time activity feed showing recent patrol events.
 * Features:
 * - Alerts pinned to top
 * - Filter toggle (All / Alerts Only)
 * - Relative timestamps
 * - Scrollable event list
 *
 * Visual Design:
 * ┌─ ACTIVITY ──────────────────────────┐
 * │  [All] [Alerts Only (2)]            │
 * │  Updated 2m ago                     │
 * │                                     │
 * │  CRITICAL (1)            [red bg]   │
 * │  Emergency SOS — John Doe           │
 * │  [Locate] [Contact] [Dismiss]       │
 * │                                     │
 * │  NEEDS ATTENTION (1)     [yellow]   │
 * │  No Activity — Tom Wilson           │
 * │  [View] [Contact] [Dismiss]         │
 * │  ─────────────────────────────────  │
 * │  GUARD ACTIVITY (3)                 │
 * │  ▼ Jane Smith    ✓ Clear    5m ago │
 * │  │ Downtown Office                  │
 * │  ├─────────────────────────────     │
 * │  │ ✓ Completed patrol       5m ago │
 * │  │ 📍 Checkpoint: Lobby    8m ago  │
 * │  │ ▶ Started patrol        15m ago │
 * │  └─────────────────────────────     │
 * │  ▶ Mike Jones    ⏳ 5/8    10m ago │
 * │  │ Main Campus                      │
 * └─────────────────────────────────────┘
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VideocamIcon from '@mui/icons-material/Videocam';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import styles from './ActivityFeed.module.css';
import classNames from 'classnames';
import {
  DispatchAlert,
  AlertAction,
  AlertCoordinates,
} from './AlertsBar';

// ============================================
// Types
// ============================================

/** Event types in the activity feed */
export type ActivityEventType =
  | 'alert'
  | 'patrol_started'
  | 'patrol_completed'
  | 'checkpoint_completed'
  | 'guard_arrived'
  | 'guard_departed';

/** Activity event data structure */
export interface ActivityEvent {
  eventId: string;
  type: ActivityEventType;
  guardId: number;
  guardName: string;
  timestamp: string;
  details: {
    patrolId?: number;
    scheduleAssignmentId?: number;
    siteName?: string;
    checkpointName?: string;
    progress?: string; // "3/8"
    alertType?: string;
    alertMessage?: string;
    patrolStatus?: 'pending' | 'in_progress' | 'completed';
  };
}

/** Event types that can open patrol details */
export const CLICKABLE_EVENT_TYPES: ActivityEventType[] = [
  'patrol_started',
  'patrol_completed',
  'checkpoint_completed',
];

/** Check if an event type is clickable (opens patrol detail) */
export function isClickableEvent(type: ActivityEventType): boolean {
  return CLICKABLE_EVENT_TYPES.includes(type);
}

/** Filter options */
export type ActivityFilter = 'all' | 'alerts';

// ============================================
// Constants
// ============================================

/** Event type configuration - uses MUI theme palette tokens */
const EVENT_CONFIG: Record<
  ActivityEventType,
  { icon: React.ElementType; color: string; label: string }
> = {
  alert: { icon: WarningIcon, color: 'error.main', label: 'Alert' },
  patrol_completed: { icon: CheckCircleIcon, color: 'success.main', label: 'Completed' },
  checkpoint_completed: { icon: LocationOnIcon, color: 'info.main', label: 'Checkpoint' },
  patrol_started: { icon: PlayArrowIcon, color: 'info.main', label: 'Started' },
  guard_arrived: { icon: PersonIcon, color: 'success.main', label: 'Arrived' },
  guard_departed: { icon: LogoutIcon, color: 'warning.main', label: 'Departed' },
};

// ============================================
// Guard Accordion Types & Constants
// ============================================

/** Badge variant for guard accordion header */
type GuardBadgeVariant = 'sos' | 'critical' | 'warning' | 'low_battery' | 'progress' | 'clear' | 'default';

interface GuardBadgeInfo {
  variant: GuardBadgeVariant;
  label: string;
  color: string;
  bgColor: string;
}

/** Sort priority for guard groups (lower = higher priority) */
const GUARD_SORT_PRIORITY: Record<GuardBadgeVariant, number> = {
  sos: 0,
  critical: 1,
  warning: 2,
  low_battery: 3,
  progress: 4,
  clear: 5,
  default: 6,
};

/** Badge display configuration - uses MUI theme palette tokens for sx resolution */
const GUARD_BADGE_CONFIG: Record<GuardBadgeVariant, { variant: GuardBadgeVariant; color: string; bgColor: string }> = {
  sos:         { variant: 'sos',         color: 'error.contrastText',   bgColor: 'error.main' },
  critical:    { variant: 'critical',    color: 'error.contrastText',   bgColor: 'error.main' },
  warning:     { variant: 'warning',     color: 'warning.contrastText', bgColor: 'warning.main' },
  low_battery: { variant: 'low_battery', color: 'warning.contrastText', bgColor: 'warning.main' },
  progress:    { variant: 'progress',    color: 'primary.contrastText', bgColor: 'primary.main' },
  clear:       { variant: 'clear',       color: 'success.contrastText', bgColor: 'success.main' },
  default:     { variant: 'default',     color: 'text.secondary',       bgColor: 'grey.300' },
};

/** Grouped guard data for accordion rendering */
interface GuardGroup {
  guardId: number;
  guardName: string;
  events: ActivityEvent[];
  mostRecentTimestamp: string;
  badge: GuardBadgeInfo;
  latestSiteName: string;
  latestContext: string;
}

// ============================================
// Utility Functions
// ============================================

// Import and re-export formatRelativeTime from utils
// (moved to utils.ts to break circular dependency with AlertsBar)
import { formatRelativeTime } from './utils';
export { formatRelativeTime };

// ============================================
// Sub-components
// ============================================

interface ActivityEventItemProps {
  event: ActivityEvent;
  isAlert?: boolean;
  onClick?: (event: ActivityEvent) => void;
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const ActivityEventItem: React.FC<ActivityEventItemProps> = ({ event, isAlert = false, onClick, onSiteClick }) => {
  const config = EVENT_CONFIG[event.type];
  const Icon = config.icon;
  const relativeTime = formatRelativeTime(event.timestamp);
  const isClickable = isClickableEvent(event.type) && onClick;
  // TODO: addressId not available in event details — siteName rendered as plain text

  // Build description based on event type
  let description = '';
  if (event.type === 'alert') {
    description = event.details.alertMessage || 'No activity detected';
  } else if (event.type === 'checkpoint_completed') {
    description = event.details.checkpointName || '';
    if (event.details.progress) {
      description += description ? ` • ${event.details.progress}` : event.details.progress;
    }
  } else {
    description = event.details.siteName || '';
  }

  const handleClick = () => {
    if (isClickable) {
      onClick(event);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(event);
    }
  };

  return (
    <Box
      className={classNames(styles.eventItem, {
        [styles.alertItem]: isAlert,
        [styles.clickable]: isClickable,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `View patrol details for ${event.guardName}` : undefined}
    >
      <Box className={styles.eventIcon} sx={{ color: config.color }}>
        <Icon />
      </Box>
      <Box className={styles.eventContent}>
        <Box className={styles.eventHeader}>
          <Typography variant="body2" className={styles.eventTitle}>
            <Box component="span" className={styles.eventLabel} sx={{ color: config.color }}>
              {config.label}:
            </Box>{' '}
            {event.guardName}
          </Typography>
          <Tooltip title={new Date(event.timestamp).toLocaleString()}>
            <Typography variant="caption" className={styles.eventTime}>
              {relativeTime}
            </Typography>
          </Tooltip>
        </Box>
        {description && (
          <Typography variant="caption" className={styles.eventDescription}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const EventSkeleton: React.FC = () => (
  <Box className={styles.eventItem}>
    <Skeleton variant="circular" width={24} height={24} animation="wave" />
    <Box className={styles.eventContent}>
      <Skeleton variant="text" width="70%" height={20} animation="wave" />
      <Skeleton variant="text" width="50%" height={16} animation="wave" />
    </Box>
  </Box>
);

// ============================================
// Dispatch Alert Item Component
// ============================================

/** Parse coordinates from alert message */
function parseCoordinatesFromMessage(message: string): AlertCoordinates | null {
  const coordMatch = message.match(/\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/);
  if (coordMatch) {
    const latitude = parseFloat(coordMatch[1]);
    const longitude = parseFloat(coordMatch[2]);
    if (!isNaN(latitude) && !isNaN(longitude)) {
      return { latitude, longitude };
    }
  }
  return null;
}

/** Remove coordinates from message for cleaner display */
function cleanAlertMessage(message: string): string {
  return message
    .replace(/\s*at coordinates\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)/gi, '')
    .replace(/\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\)\s*/g, '')
    .trim();
}

/** Alert type display config */
const ALERT_TYPE_LABELS: Record<string, string> = {
  no_activity: 'No Activity',
  left_site: 'Left Site',
  low_battery: 'Low Battery',
  incomplete_patrol: 'Incomplete Patrol',
  stale_patrol: 'Stale Patrol',
  emergency_sos: 'Emergency SOS',
  vms_incident: 'Camera Incident',
};

/**
 * Derive the most important badge for a guard based on their
 * active dispatch alerts and latest event.
 */
function deriveGuardBadge(
  guardId: number,
  activeDispatchAlerts: DispatchAlert[],
  latestEvent: ActivityEvent | undefined
): GuardBadgeInfo {
  const guardAlerts = activeDispatchAlerts.filter(a => a.guardId === guardId);

  const hasSOS = guardAlerts.some(a => a.type === 'emergency_sos');
  if (hasSOS) {
    return { ...GUARD_BADGE_CONFIG.sos, label: 'SOS' };
  }

  const critAlert = guardAlerts.find(a => a.severity === 'critical');
  if (critAlert) {
    return { ...GUARD_BADGE_CONFIG.critical, label: ALERT_TYPE_LABELS[critAlert.type] || 'Alert' };
  }

  const battAlert = guardAlerts.find(a => a.type === 'low_battery');
  if (battAlert) {
    const pct = battAlert.details.batteryPercent;
    return { ...GUARD_BADGE_CONFIG.low_battery, label: pct != null ? `${pct}%` : 'Low' };
  }

  const warnAlert = guardAlerts.find(a => a.severity !== 'critical');
  if (warnAlert) {
    return { ...GUARD_BADGE_CONFIG.warning, label: ALERT_TYPE_LABELS[warnAlert.type] || 'Issue' };
  }

  // No dispatch alerts — derive from latest event
  if (latestEvent) {
    if (latestEvent.type === 'checkpoint_completed' && latestEvent.details.progress) {
      return { ...GUARD_BADGE_CONFIG.progress, label: latestEvent.details.progress };
    }
    if (latestEvent.type === 'patrol_completed') {
      return { ...GUARD_BADGE_CONFIG.clear, label: 'Clear' };
    }
  }

  return { ...GUARD_BADGE_CONFIG.default, label: '\u2713' };
}

interface DispatchAlertItemProps {
  alert: DispatchAlert;
  onAction?: (alertId: string, action: AlertAction, alert: DispatchAlert, coordinates?: AlertCoordinates) => void;
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const DispatchAlertItem: React.FC<DispatchAlertItemProps> = ({ alert, onAction, onSiteClick }) => {
  const isCritical = alert.severity === 'critical';
  const isVmsAlert = alert.type === 'vms_incident';

  // Detect emergency SOS (may come as different types)
  const isEmergencySOS = alert.type === 'emergency_sos' || (
    isCritical && (
      (alert.message || '').toLowerCase().includes('emergency sos') ||
      (alert.message || '').toLowerCase().includes('sos alert')
    )
  );

  const displayLabel = isEmergencySOS ? 'Emergency SOS' : (ALERT_TYPE_LABELS[alert.type] || alert.type);
  const coordinates = alert.message ? parseCoordinatesFromMessage(alert.message) : null;
  const cleanMessage = alert.message ? cleanAlertMessage(alert.message) : '';
  const relativeTime = formatRelativeTime(alert.createdAt);

  const handleAction = (action: AlertAction) => {
    onAction?.(alert.alertId, action, alert, action === 'locate' ? coordinates || undefined : undefined);
  };

  return (
    <Box
      className={classNames(styles.dispatchAlert, {
        [styles.criticalAlert]: isCritical,
        [styles.warningAlert]: !isCritical,
        [styles.emergencySos]: isEmergencySOS,
      })}
    >
      {/* Alert Icon & Type */}
      <Box className={styles.dispatchAlertHeader}>
        <Box className={styles.dispatchAlertIcon}>
          {isVmsAlert ? <VideocamIcon /> : isCritical ? <ErrorIcon /> : <WarningIcon />}
        </Box>
        <Typography variant="body2" className={styles.dispatchAlertType}>
          {displayLabel}
        </Typography>
        <Box className={classNames(styles.severityBadge, {
          [styles.criticalSeverity]: isCritical,
          [styles.warningSeverity]: !isCritical,
        })}>
          {alert.severity}
        </Box>
        <Typography variant="caption" className={styles.dispatchAlertTime}>
          {relativeTime}
        </Typography>
      </Box>

      {/* Identity line: camera name + site for VMS, guard name + site for guards */}
      <Typography variant="body2" className={styles.dispatchAlertGuard}>
        {isVmsAlert
          ? (alert.details.cameraName || 'Camera')
          : alert.guardName}
        {isVmsAlert && alert.details.incidentType && ` • ${alert.details.incidentType}`}
        {alert.details.siteName && (
          <>
            {' \u2022 '}
            {alert.details.siteId && onSiteClick ? (
              <Link
                component="button"
                variant="body2"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSiteClick(alert.details.siteId!, alert.details.siteName!);
                }}
                sx={{ verticalAlign: 'baseline' }}
              >
                {alert.details.siteName}
              </Link>
            ) : (
              alert.details.siteName
            )}
          </>
        )}
      </Typography>

      {/* Message */}
      {cleanMessage && cleanMessage !== displayLabel && (
        <Typography variant="caption" className={styles.dispatchAlertMessage}>
          {cleanMessage}
        </Typography>
      )}

      {/* Location Links (if coordinates) */}
      {coordinates && (
        <Box className={styles.dispatchAlertLocation}>
          <Tooltip title="Show on map">
            <Box
              component="span"
              className={styles.locationButton}
              onClick={() => handleAction('locate')}
              role="button"
              tabIndex={0}
            >
              <MapIcon fontSize="small" />
              <span>Show on Map</span>
            </Box>
          </Tooltip>
          <Tooltip title="Open in Google Maps">
            <Box
              component="span"
              className={styles.locationButton}
              onClick={() => window.open(`https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`, '_blank')}
              role="button"
              tabIndex={0}
            >
              <OpenInNewIcon fontSize="small" />
              <span>Google Maps</span>
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Action Buttons */}
      <Box className={styles.dispatchAlertActions}>
        {isVmsAlert ? (
          <>
            <Tooltip title="View camera live stream">
              <Box
                component="span"
                className={styles.alertActionButton}
                onClick={() => handleAction('view')}
                role="button"
                tabIndex={0}
              >
                <VideocamIcon fontSize="small" />
                <span>View Camera</span>
              </Box>
            </Tooltip>
            <Tooltip title="View all incidents for this camera">
              <Box
                component="span"
                className={styles.alertActionButton}
                onClick={() => handleAction('incidents')}
                role="button"
                tabIndex={0}
              >
                <OpenInNewIcon fontSize="small" />
                <span>Incidents</span>
              </Box>
            </Tooltip>
            <Tooltip title="Dispatch guard to camera incident">
              <Box
                component="span"
                className={styles.alertActionButton}
                onClick={() => handleAction('dispatch')}
                role="button"
                tabIndex={0}
              >
                <VisibilityIcon fontSize="small" />
                <span>Dispatch</span>
              </Box>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="View guard details">
              <Box
                component="span"
                className={styles.alertActionButton}
                onClick={() => handleAction('view')}
                role="button"
                tabIndex={0}
              >
                <VisibilityIcon fontSize="small" />
                <span>View</span>
              </Box>
            </Tooltip>
            <Tooltip title="Contact guard">
              <Box
                component="span"
                className={styles.alertActionButton}
                onClick={() => handleAction('contact')}
                role="button"
                tabIndex={0}
              >
                <PhoneIcon fontSize="small" />
                <span>Contact</span>
              </Box>
            </Tooltip>
          </>
        )}
        <Tooltip title="Dismiss alert">
          <Box
            component="span"
            className={classNames(styles.alertActionButton, styles.dismissAction)}
            onClick={() => handleAction('dismiss')}
            role="button"
            tabIndex={0}
          >
            <CloseIcon fontSize="small" />
            <span>Dismiss</span>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ============================================
// Guard Accordion Component
// ============================================

interface GuardAccordionProps {
  group: GuardGroup;
  expanded: boolean;
  onExpandChange: (guardId: number, expanded: boolean) => void;
  onItemClick?: (event: ActivityEvent) => void;
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const GuardAccordion: React.FC<GuardAccordionProps> = ({
  group,
  expanded,
  onExpandChange,
  onItemClick,
  onSiteClick,
}) => {
  const relativeTime = formatRelativeTime(group.mostRecentTimestamp);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandChange(group.guardId, isExpanded)}
      className={styles.guardAccordion}
      disableGutters
      sx={{
        backgroundColor: expanded ? 'action.hover' : 'transparent',
        transition: 'background-color 0.2s ease',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={styles.guardSummary}
      >
        <Box className={styles.guardSummaryContent}>
          <Box className={styles.guardSummaryRow}>
            <Typography variant="body2" className={styles.guardName}>
              {group.guardName}
            </Typography>
            {group.badge.label && (
              <Box
                component="span"
                className={styles.guardBadge}
                sx={{
                  backgroundColor: group.badge.bgColor,
                  color: group.badge.color,
                }}
              >
                {group.badge.label}
              </Box>
            )}
            <Tooltip title={new Date(group.mostRecentTimestamp).toLocaleString()}>
              <Typography variant="caption" className={styles.guardTime}>
                {relativeTime}
              </Typography>
            </Tooltip>
          </Box>
          {group.latestSiteName && (
            <Typography variant="caption" className={styles.guardSubtitle}>
              {/* TODO: addressId not available in event details — siteName rendered as plain text */}
              {group.latestSiteName}
              {group.latestContext && ` \u2022 ${group.latestContext}`}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails className={styles.guardDetails}>
        {group.events.map((event) => (
          <ActivityEventItem
            key={event.eventId}
            event={event}
            isAlert={event.type === 'alert'}
            onClick={onItemClick}
            onSiteClick={onSiteClick}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// ============================================
// Main Component
// ============================================

export interface ActivityFeedProps {
  /** Events to display */
  events: ActivityEvent[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Current filter */
  filter?: ActivityFilter;
  /** Callback when filter changes */
  onFilterChange?: (filter: ActivityFilter) => void;
  /** Callback to refresh events */
  onRefresh?: () => void;
  /** Callback when a patrol-related event is clicked */
  onItemClick?: (event: ActivityEvent) => void;
  /** Last update timestamp */
  lastUpdate?: Date | null;
  /** Maximum height for the feed */
  maxHeight?: number | string;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Title override */
  title?: string;
  /** Dispatch alerts to display prominently */
  dispatchAlerts?: DispatchAlert[];
  /** Callback when a dispatch alert action is taken */
  onAlertAction?: (alertId: string, action: AlertAction, alert: DispatchAlert, coordinates?: AlertCoordinates) => void;
  /** Callback when a site name is clicked to open site info */
  onSiteClick?: (addressId: number, siteName: string) => void;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  events,
  isLoading = false,
  error = null,
  filter = 'all',
  onFilterChange,
  onRefresh,
  onItemClick,
  lastUpdate,
  maxHeight = 400,
  showHeader = true,
  title = 'Activity',
  dispatchAlerts = [],
  onAlertAction,
  onSiteClick,
}) => {
  // Separate alerts from other events AND build guard groups
  const { alerts, filteredEvents, guardGroups } = useMemo(() => {
    const alerts = events.filter((e) => e.type === 'alert');
    const regularEvents = events.filter((e) => e.type !== 'alert');

    // Sort by timestamp (most recent first)
    const sortedAlerts = [...alerts].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const sortedRegular = [...regularEvents].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    // Build guard groups: group ALL events by guardId
    const groupMap = new Map<number, { guardId: number; guardName: string; events: ActivityEvent[] }>();
    for (const event of events) {
      let group = groupMap.get(event.guardId);
      if (!group) {
        group = { guardId: event.guardId, guardName: event.guardName, events: [] };
        groupMap.set(event.guardId, group);
      }
      group.events.push(event);
    }

    // Derive metadata and sort each group
    const activeAlerts = dispatchAlerts.filter((a) => !a.dismissedAt);
    const groups: GuardGroup[] = Array.from(groupMap.values()).map((g) => {
      const sortedEvents = [...g.events].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      const latest = sortedEvents[0];
      const badge = deriveGuardBadge(g.guardId, activeAlerts, latest);

      const latestSiteName = latest?.details.siteName || '';
      const latestContext = (latest?.type === 'checkpoint_completed' && latest.details.progress)
        ? latest.details.progress
        : '';

      return {
        guardId: g.guardId,
        guardName: g.guardName,
        events: sortedEvents,
        mostRecentTimestamp: latest?.timestamp || new Date(0).toISOString(),
        badge,
        latestSiteName,
        latestContext,
      };
    });

    // Sort guard groups: badge priority first, then by most recent event
    groups.sort((a, b) => {
      const priorityA = GUARD_SORT_PRIORITY[a.badge.variant];
      const priorityB = GUARD_SORT_PRIORITY[b.badge.variant];
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(b.mostRecentTimestamp).getTime() - new Date(a.mostRecentTimestamp).getTime();
    });

    return {
      alerts: sortedAlerts,
      regularEvents: sortedRegular,
      filteredEvents: filter === 'alerts' ? sortedAlerts : [...sortedAlerts, ...sortedRegular],
      guardGroups: groups,
    };
  }, [events, filter, dispatchAlerts]);

  // Process dispatch alerts - separate critical from warning, filter dismissed
  const { criticalAlerts, warningAlerts, activeDispatchAlerts } = useMemo(() => {
    const active = dispatchAlerts.filter((a) => !a.dismissedAt);
    const critical = active.filter((a) => a.severity === 'critical');
    const warning = active.filter((a) => a.severity !== 'critical');
    return {
      criticalAlerts: critical,
      warningAlerts: warning,
      activeDispatchAlerts: active,
    };
  }, [dispatchAlerts]);

  // Total alert count for badge (dispatch alerts + activity alerts)
  const totalAlertCount = activeDispatchAlerts.length + alerts.length;

  // Search query state (Feature C)
  const [searchQuery, setSearchQuery] = useState('');

  // Batch dismiss dialog state (Feature A)
  const [batchDismissDialogOpen, setBatchDismissDialogOpen] = useState(false);

  // Guard accordion expansion state: multiple can be open
  const [expandedGuardIds, setExpandedGuardIds] = useState<Set<number>>(new Set());

  // Auto-expand guards with critical alerts
  useEffect(() => {
    const criticalGuardIds = activeDispatchAlerts
      .filter(a => a.severity === 'critical')
      .map(a => a.guardId);
    if (criticalGuardIds.length > 0) {
      setExpandedGuardIds(prev => {
        const next = new Set(prev);
        criticalGuardIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [activeDispatchAlerts]);

  const handleGuardExpand = useCallback((guardId: number, expanded: boolean) => {
    setExpandedGuardIds(prev => {
      const next = new Set(prev);
      if (expanded) {
        next.add(guardId);
      } else {
        next.delete(guardId);
      }
      return next;
    });
  }, []);

  // Filter guard groups, dispatch alerts, and activity alerts based on search query (Feature C)
  const {
    filteredGuardGroups,
    filteredCriticalAlerts,
    filteredWarningAlerts,
    filteredActivityAlerts,
    searchResultCount,
  } = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        filteredGuardGroups: guardGroups,
        filteredCriticalAlerts: criticalAlerts,
        filteredWarningAlerts: warningAlerts,
        filteredActivityAlerts: alerts,
        searchResultCount: null,
      };
    }
    const q = searchQuery.toLowerCase().trim();

    const fGroups = guardGroups.filter((group) => {
      if (group.guardName.toLowerCase().includes(q)) return true;
      if (group.latestSiteName.toLowerCase().includes(q)) return true;
      return group.events.some(
        (e) =>
          e.details.siteName?.toLowerCase().includes(q) ||
          e.details.checkpointName?.toLowerCase().includes(q)
      );
    });

    const fCritical = criticalAlerts.filter(
      (alert) =>
        alert.guardName.toLowerCase().includes(q) ||
        alert.details.siteName?.toLowerCase().includes(q) ||
        alert.details.cameraName?.toLowerCase().includes(q) ||
        alert.message?.toLowerCase().includes(q)
    );

    const fWarning = warningAlerts.filter(
      (alert) =>
        alert.guardName.toLowerCase().includes(q) ||
        alert.details.siteName?.toLowerCase().includes(q) ||
        alert.details.cameraName?.toLowerCase().includes(q) ||
        alert.message?.toLowerCase().includes(q)
    );

    const fActivityAlerts = alerts.filter(
      (event) =>
        event.guardName.toLowerCase().includes(q) ||
        event.details.siteName?.toLowerCase().includes(q) ||
        event.details.alertMessage?.toLowerCase().includes(q)
    );

    return {
      filteredGuardGroups: fGroups,
      filteredCriticalAlerts: fCritical,
      filteredWarningAlerts: fWarning,
      filteredActivityAlerts: fActivityAlerts,
      searchResultCount: fGroups.length + fCritical.length + fWarning.length + fActivityAlerts.length,
    };
  }, [searchQuery, guardGroups, criticalAlerts, warningAlerts, alerts]);

  // Batch dismiss all warning alerts (Feature A)
  const handleBatchDismiss = useCallback(() => {
    warningAlerts.forEach((alert) => {
      onAlertAction?.(alert.alertId, 'dismiss', alert);
    });
    setBatchDismissDialogOpen(false);
  }, [warningAlerts, onAlertAction]);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: ActivityFilter | null
  ) => {
    if (newFilter !== null) {
      onFilterChange?.(newFilter);
    }
  };

  return (
    <Box className={styles.feed} style={{ maxHeight }}>
      {/* Header */}
      {showHeader && (
        <Box className={styles.header}>
          <Typography variant="subtitle1" className={styles.title}>
            {title}
          </Typography>
          {onRefresh && (
            <Tooltip title="Refresh">
              <Box
                component="span"
                className={classNames(styles.refreshButton, { [styles.spinning]: isLoading })}
                onClick={onRefresh}
                role="button"
                tabIndex={0}
              >
                <RefreshIcon fontSize="small" />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Search (Feature C) */}
      <Box className={styles.searchRow}>
        <TextField
          placeholder="Search guards, sites..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        {searchQuery.trim() && searchResultCount !== null && (
          <Typography variant="caption" className={styles.searchResultCount}>
            {searchResultCount} result{searchResultCount !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Filter Toggle */}
      <Box className={styles.filterRow}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          className={styles.filterToggle}
        >
          <ToggleButton value="all" className={styles.filterButton}>
            All
          </ToggleButton>
          <ToggleButton value="alerts" className={styles.filterButton}>
            Alerts Only
            {totalAlertCount > 0 && (
              <Box component="span" className={classNames(styles.alertBadge, {
                [styles.criticalBadge]: criticalAlerts.length > 0,
              })}>
                {totalAlertCount}
              </Box>
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Last Update */}
      {lastUpdate && (
        <Typography variant="caption" className={styles.lastUpdate}>
          Updated {formatRelativeTime(lastUpdate.toISOString())}
        </Typography>
      )}

      {/* Loading State */}
      {isLoading && events.length === 0 && (
        <Box className={styles.eventList}>
          <EventSkeleton />
          <EventSkeleton />
          <EventSkeleton />
          <EventSkeleton />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box className={styles.errorState}>
          <WarningIcon className={styles.errorIcon} />
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      {/* Empty State - only when no events AND no dispatch alerts */}
      {!isLoading && !error && events.length === 0 && activeDispatchAlerts.length === 0 && (
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No recent activity
          </Typography>
        </Box>
      )}

      {/* Event List */}
      {!error && (filteredEvents.length > 0 || activeDispatchAlerts.length > 0) && (
        <Box className={styles.eventList}>
          {/* CRITICAL DISPATCH ALERTS - Super prominent at top */}
          {filteredCriticalAlerts.length > 0 && (
            <Box className={styles.criticalAlertsSection}>
              <Box className={styles.criticalSectionHeader}>
                <ErrorIcon className={styles.criticalSectionIcon} />
                <Typography variant="caption" className={styles.criticalSectionTitle}>
                  CRITICAL ({filteredCriticalAlerts.length})
                </Typography>
              </Box>
              {filteredCriticalAlerts.map((alert) => (
                <DispatchAlertItem
                  key={alert.alertId}
                  alert={alert}
                  onAction={onAlertAction}
                  onSiteClick={onSiteClick}
                />
              ))}
            </Box>
          )}

          {/* WARNING DISPATCH ALERTS */}
          {filter === 'all' && filteredWarningAlerts.length > 0 && (
            <>
              <Box className={styles.sectionHeader}>
                <WarningIcon className={styles.sectionIcon} sx={{ color: 'warning.main' }} />
                <Typography variant="caption" className={styles.sectionTitle}>
                  Needs Attention ({filteredWarningAlerts.length})
                </Typography>
                {warningAlerts.length > 0 && onAlertAction && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<CloseIcon />}
                    onClick={() => setBatchDismissDialogOpen(true)}
                    className={styles.batchDismissButton}
                  >
                    Dismiss All
                  </Button>
                )}
              </Box>
              {filteredWarningAlerts.map((alert) => (
                <DispatchAlertItem
                  key={alert.alertId}
                  alert={alert}
                  onAction={onAlertAction}
                  onSiteClick={onSiteClick}
                />
              ))}
              {filteredGuardGroups.length > 0 && <Box className={styles.sectionDivider} />}
            </>
          )}

          {/* Guard Activity Accordions (grouped by guard) */}
          {filter === 'all' && filteredGuardGroups.length > 0 && (
            <>
              <Box className={styles.sectionHeader}>
                <Typography variant="caption" className={styles.sectionTitle}>
                  Guard Activity ({filteredGuardGroups.length})
                </Typography>
              </Box>
              {filteredGuardGroups.map((group) => (
                <GuardAccordion
                  key={group.guardId}
                  group={group}
                  expanded={expandedGuardIds.has(group.guardId)}
                  onExpandChange={handleGuardExpand}
                  onItemClick={onItemClick}
                  onSiteClick={onSiteClick}
                />
              ))}
            </>
          )}

          {/* Alerts Only View - Show all dispatch alerts + activity alerts */}
          {filter === 'alerts' && (
            <>
              {/* Warning dispatch alerts in alerts-only view */}
              {filteredWarningAlerts.map((alert) => (
                <DispatchAlertItem
                  key={alert.alertId}
                  alert={alert}
                  onAction={onAlertAction}
                  onSiteClick={onSiteClick}
                />
              ))}
              {/* Activity event alerts */}
              {filteredActivityAlerts.map((event) => (
                <ActivityEventItem key={event.eventId} event={event} isAlert onClick={onItemClick} onSiteClick={onSiteClick} />
              ))}
            </>
          )}

          {/* No alerts message when filtering */}
          {filter === 'alerts' && totalAlertCount === 0 && (
            <Box className={styles.emptyState}>
              <Typography variant="body2" color="textSecondary">
                No active alerts
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Batch Dismiss Confirmation Dialog (Feature A) */}
      <Dialog
        open={batchDismissDialogOpen}
        onClose={() => setBatchDismissDialogOpen(false)}
      >
        <DialogTitle>Dismiss Warning Alerts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dismiss all {warningAlerts.length} warning alert{warningAlerts.length !== 1 ? 's' : ''}? Critical alerts will not be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDismissDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBatchDismiss} color="primary" variant="contained">
            Dismiss All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityFeed;
